import { Spacing } from '@persona-id/design-tokens';
import { ReactNode } from 'react';
import { COLORS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import IconChevron from 'images/icons/chevron.svg';
import TextBase from 'inquiry/components/TextBase';
import { colorCss, sizeCss } from 'lib/inquiryStyles';
import { SerializedStepGovernmentIdStyles } from 'lib/interfaces/StepsConfig';
import { HolderClient } from 'verify/lib/clients/HolderClient';

interface VerticalOptionStyles {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  border?: SerializedStepGovernmentIdStyles['selectOption']['border'];
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  backgroundColor?: SerializedStepGovernmentIdStyles['selectOption']['backgroundColor'];
}

interface VerticalOptionChevronStyle {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  stroke: SerializedStepGovernmentIdStyles['selectOption']['icon']['stroke'];
}

const OptionBlock = styled.button<{
  buttonType: 'outline' | 'outline_light';
  styles?: VerticalOptionStyles;
}>`
  /* Button style reset */
  border: 0;
  margin: 0;
  padding: 0;
  background: transparent;
  box-sizing: border-box;
  color: inherit;
  text-align: inherit;

  /* Component styles */
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;

  ${() => {
    /**
     * NOTE: This is a special case where we stretch UI edge-to-edge by
     * default. This makes styling inline flow hard because you can't
     * left-align content, so we disable edge-to-edge stretching for inline
     * flow.
     */
    const edgeToEdgePaddingLeft = HolderClient.isInline
      ? '0px'
      : `-${HolderClient.widgetPadding.left}px`;
    const edgeToEdgePaddingRight = HolderClient.isInline
      ? '0px'
      : `-${HolderClient.widgetPadding.right}px`;

    return `
      width: calc(100% - ${edgeToEdgePaddingLeft} - ${edgeToEdgePaddingRight});
      margin-left: ${edgeToEdgePaddingLeft};
      margin-right: ${edgeToEdgePaddingRight};
    `;
  }}

  ${({ styles, buttonType }) => {
    const outlineBorder = (fallbackColor: string): string =>
      styles?.border != null
        ? `${sizeCss(styles.border.width.top)} solid ${colorCss(styles.border.color)}`
        : `1px solid ${fallbackColor}`;

    switch (buttonType) {
      case 'outline':
        return css`
          padding-top: ${Spacing[4]};
          padding-bottom: ${Spacing[4]};
          padding-left: ${Spacing[5]};
          padding-right: ${Spacing[5]};
          border-top: ${outlineBorder(COLORS.gray50)};
          :last-of-type {
            border-bottom: ${outlineBorder(COLORS.gray50)};
          }

          :hover,
          :focus {
            background-color: ${({ theme }) =>
              styles?.backgroundColor
                ? colorCss(styles.backgroundColor.focused)
                : theme.colorDropdownOption};
          }
        `;
      case 'outline_light':
        // This custom theme is specifically for DoorDash. That's why various variables are hardcoded.
        return css`
          padding-top: 18px;
          padding-bottom: 18px;
          padding-left: 0;
          padding-right: ${Spacing[5]};
          border-top: ${({ theme }) => {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            return outlineBorder(theme.colorDivider);
          }};
          border-bottom: ${({ theme }) => {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            return outlineBorder(theme.colorDivider);
          }};
          & + & {
            border-top: none;
          }

          color: ${({ theme }) => theme.colorFontTitle};
          margin-left: 0;
          width: calc(100% + ${HolderClient.widgetPadding.left}px);
          font-weight: 500;

          /* Note no hover styles; focus-within is keyboard only for a11y */
          :focus-within {
            background-color: ${({ theme }) =>
              styles?.backgroundColor
                ? colorCss(styles.backgroundColor.focused)
                : theme.colorDropdownOption};
          }
        `;
    }
  }}
`;

const Text = styled('span')`
  flex-grow: 1;
`;

const StyledChevron = styled(IconChevron)<{ styles: VerticalOptionChevronStyle }>`
  path {
    stroke: ${({ styles, theme }) =>
      styles?.stroke?.color != null ? colorCss(styles.stroke.color) : theme.colorFontTitle};
  }
`;

interface VerticalOptionProps {
  id: string;
  'data-test'?: string;
  buttonType?: 'outline' | 'outline_light';
  onClick: () => void;
  icon: ReactNode | null;
  children: ReactNode;
  styles?: SerializedStepGovernmentIdStyles['selectOption'];
}

export default function VerticalOption({
  id,
  'data-test': dataTest,
  buttonType = 'outline',
  onClick,
  icon,
  children,
  styles,
}: VerticalOptionProps) {
  return (
    <OptionBlock
      id={id}
      data-test={dataTest}
      onClick={onClick}
      type='button'
      buttonType={buttonType}
      styles={{
        backgroundColor: styles?.backgroundColor,
        border: styles?.border,
      }}
    >
      {icon}
      <Text>
        {styles != null ? <TextBase styles={styles.textStyle}>{children}</TextBase> : children}
      </Text>
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <StyledChevron styles={styles?.chevron} />
    </OptionBlock>
  );
}
